import { createReducer, on } from '@ngrx/store';
import {
  TranscodingPresetsState,
  initialTranscodingPresetsState,
  transcodingPresetsAdapter,
} from './transcoding-presets.state';
import * as TranscodingPresetsActions from './transcoding-presets.actions';

/**
 * Reducer for transcoding presets state management
 * Handles all state transitions based on dispatched actions
 */
export const transcodingPresetsReducer = createReducer(
  initialTranscodingPresetsState,

  // ==================== Load All Presets ====================

  on(TranscodingPresetsActions.loadTranscodingPresets, (state: TranscodingPresetsState): TranscodingPresetsState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    TranscodingPresetsActions.loadTranscodingPresetsSuccess,
    (state: TranscodingPresetsState, { presets }): TranscodingPresetsState =>
      transcodingPresetsAdapter.setAll(presets, {
        ...state,
        loading: false,
        loaded: true,
        lastUpdated: Date.now(),
        error: null,
      })
  ),

  on(
    TranscodingPresetsActions.loadTranscodingPresetsFailure,
    (state, { error }): TranscodingPresetsState => ({
      ...state,
      loading: false,
      error,
    })
  ),

  // ==================== Load Single Preset by ID ====================

  on(
    TranscodingPresetsActions.loadTranscodingPresetById,
    (state, { id }): TranscodingPresetsState => ({
      ...state,
      loadingById: {
        ...state.loadingById,
        [id]: true,
      },
    })
  ),

  on(
    TranscodingPresetsActions.loadTranscodingPresetByIdSuccess,
    (state, { preset }): TranscodingPresetsState =>
      transcodingPresetsAdapter.upsertOne(preset, {
        ...state,
        loadingById: {
          ...state.loadingById,
          [preset.id]: false,
        },
      })
  ),

  on(
    TranscodingPresetsActions.loadTranscodingPresetByIdFailure,
    (state, { id, error }): TranscodingPresetsState => ({
      ...state,
      loadingById: {
        ...state.loadingById,
        [id]: false,
      },
      error,
    })
  ),

  // ==================== Create Preset ====================

  on(
    TranscodingPresetsActions.createTranscodingPreset,
    (state): TranscodingPresetsState => ({
      ...state,
      creating: true,
      createError: null,
    })
  ),

  on(
    TranscodingPresetsActions.createTranscodingPresetSuccess,
    (state, { preset }): TranscodingPresetsState =>
      transcodingPresetsAdapter.addOne(preset, {
        ...state,
        creating: false,
        createError: null,
        selectedPresetId: preset.id,
      })
  ),

  on(
    TranscodingPresetsActions.createTranscodingPresetFailure,
    (state, { error }): TranscodingPresetsState => ({
      ...state,
      creating: false,
      createError: error,
    })
  ),

  // ==================== Delete Preset ====================

  on(
    TranscodingPresetsActions.deleteTranscodingPreset,
    (state, { id }): TranscodingPresetsState => ({
      ...state,
      deleting: {
        ...state.deleting,
        [id]: true,
      },
      deleteError: {
        ...state.deleteError,
        [id]: null,
      },
    })
  ),

  on(
    TranscodingPresetsActions.deleteTranscodingPresetSuccess,
    (state, { id }): TranscodingPresetsState => {
      const newState = transcodingPresetsAdapter.removeOne(id, state);

      // Clean up the tracking objects
      const { [id]: removedDeleting, ...remainingDeleting } = newState.deleting;
      const { [id]: removedDeleteError, ...remainingDeleteError } = newState.deleteError;
      const { [id]: removedUpdating, ...remainingUpdating } = newState.updating;
      const { [id]: removedUpdateError, ...remainingUpdateError } = newState.updateError;
      const { [id]: removedLoadingById, ...remainingLoadingById } = newState.loadingById;

      return {
        ...newState,
        deleting: remainingDeleting,
        deleteError: remainingDeleteError,
        updating: remainingUpdating,
        updateError: remainingUpdateError,
        loadingById: remainingLoadingById,
        selectedPresetId: newState.selectedPresetId === id ? null : newState.selectedPresetId,
      };
    }
  ),

  on(
    TranscodingPresetsActions.deleteTranscodingPresetFailure,
    (state, { id, error }): TranscodingPresetsState => ({
      ...state,
      deleting: {
        ...state.deleting,
        [id]: false,
      },
      deleteError: {
        ...state.deleteError,
        [id]: error,
      },
    })
  ),

  // ==================== UI State Management ====================

  on(
    TranscodingPresetsActions.selectTranscodingPreset,
    (state, { id }): TranscodingPresetsState => ({
      ...state,
      selectedPresetId: id,
    })
  ),

  on(
    TranscodingPresetsActions.clearTranscodingPresetErrors,
    (state): TranscodingPresetsState => ({
      ...state,
      error: null,
      createError: null,
      updateError: {},
      deleteError: {},
    })
  ),

  on(
    TranscodingPresetsActions.resetTranscodingPresetsState,
    (): TranscodingPresetsState => initialTranscodingPresetsState
  )
);
